import {
    CaretDownIcon,
    ClipboardTextIcon,
    FilePlusIcon,
    PlusIcon,
} from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { DropdownMenuItem } from '@/components';
import { Button } from '@/components/ui/Button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import type { ProcedureTemplate } from '@/types/procedure';

interface ReportsEmptyStateProps {
    procedureTemplates: ProcedureTemplate[];
    onCreateReport: (template?: ProcedureTemplate) => void;
}

export function ReportsEmptyState({
    procedureTemplates,
    onCreateReport,
}: ReportsEmptyStateProps) {
    const { t } = useTranslation(I18nNamespace.REPORTS);

    return (
        <div className="flex flex-1 min-h-[420px] items-center justify-center py-12 border border-grey-500 rounded-lg">
            <div className="w-full max-w-lg rounded-xl px-8 py-12 flex flex-col items-center text-center gap-6">
                <div className="flex flex-col items-center gap-2">
                    <ClipboardTextIcon
                        className="w-10 h-10 text-grey-200"
                        weight="fill"
                    />
                    <h2 className="text-lg text-grey-200">
                        {t('noReportsYet')}
                    </h2>
                    <p className="text-sm text-grey-300 max-w-sm">
                        {t('emptyDescription')}
                    </p>
                </div>
                <DropdownMenu modal={false}>
                    <DropdownMenuTrigger asChild>
                        <Button
                            variant="primary"
                            size="medium"
                            leadingIcon={<PlusIcon className="size-4" />}
                            trailingIcon={<CaretDownIcon className="size-4" />}
                            data-testid="reports-empty-state-create-report"
                        >
                            {t('createReport')}
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                        align="center"
                        sideOffset={8}
                        className="min-w-[250px]"
                    >
                        <div className="px-[10px] pt-[5px] pb-[8px]">
                            {procedureTemplates.map((template) => (
                                <DropdownMenuItem
                                    key={template.id}
                                    icon={ClipboardTextIcon}
                                    label={template.name}
                                    onClick={() => onCreateReport(template)}
                                />
                            ))}
                            {procedureTemplates.length > 0 ? (
                                <DropdownMenuSeparator className="mx-0" />
                            ) : null}
                            <DropdownMenuItem
                                icon={FilePlusIcon}
                                label={t('createMenu.blankReport')}
                                onClick={() => onCreateReport()}
                            />
                        </div>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        </div>
    );
}
